import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/api';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/VpnKey';
import toast from 'react-hot-toast';

// 현장 1회용 코드 — Digntec 관리자가 발급해 전화로 전달 (knowledge-hub 12.01-D007)
const STATUS = {
  active: { label: '대기', color: 'success' },
  in_use: { label: '사용 중', color: 'primary' },
  finished: { label: '종료', color: 'default' },
  expired: { label: '만료', color: 'default' },
  revoked: { label: '취소', color: 'error' },
};

function FieldCodePanel() {
  const [storeLabel, setStoreLabel] = useState('');
  const [note, setNote] = useState('');
  const [issued, setIssued] = useState(null);
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/field-kit/codes');
      setCodes(res.data.codes || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load field codes');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async () => {
    setBusy(true);
    try {
      const res = await api.post('/field-kit/codes', { storeLabel, note });
      setIssued(res.data);
      setNote('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Issue failed');
    }
    setBusy(false);
  };

  const revoke = async (id) => {
    try {
      await api.post(`/field-kit/codes/${id}/revoke`);
      if (issued?.id === id) setIssued(null);
      toast.success('Revoked');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Revoke failed');
    }
  };

  const fmt = (d) =>
    d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <KeyIcon sx={{ color: '#551FFF' }} />
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>현장 코드 발급 (Field code)</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        매장 원클릭 설치에서 WireGuard(원격관리)까지 하려면 이 코드가 필요합니다. 발급 후 전화로 불러 주세요 —
        1시간 안에 한 번만 쓸 수 있고, 입력 후 1시간 동안 그 매장 TV 여러 대에 적용됩니다.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField size="small" label="매장 (예: TLJ Allston)" value={storeLabel} onChange={(e) => setStoreLabel(e.target.value)} sx={{ minWidth: 220 }} />
        <TextField size="small" label="메모 (요청자·사유)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1 }} />
        <Button variant="contained" onClick={issue} disabled={busy} sx={{ whiteSpace: 'nowrap' }}>
          코드 발급
        </Button>
      </Stack>

      {issued && (
        <Box sx={{ p: 2, mb: 1.5, borderRadius: 2, bgcolor: 'action.hover', textAlign: 'center' }}>
          <Typography sx={{ fontSize: 40, fontWeight: 800, letterSpacing: 8, fontFamily: 'monospace' }}>
            {issued.code.slice(0, 3)} {issued.code.slice(3)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {fmt(issued.expiresAt)} 까지 · 이 화면을 닫으면 다시 볼 수 없습니다 (필요하면 새로 발급)
          </Typography>
        </Box>
      )}

      {/* 좁은 화면에서는 표만 가로 스크롤 (머리글이 한 글자씩 꺾이지 않게) */}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 680, '& th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>발급</TableCell>
              <TableCell>코드</TableCell>
              <TableCell>매장 / 메모</TableCell>
              <TableCell>발급자</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>WG 설치</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {codes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                  발급 기록 없음
                </TableCell>
              </TableRow>
            )}
            {codes.map((c) => {
              const st = STATUS[c.status] || { label: c.status, color: 'default' };
              return (
                <TableRow key={c._id}>
                  <TableCell>{fmt(c.createdAt)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>••••{c.codeHint}</TableCell>
                  <TableCell>
                    {c.storeLabel || '—'}
                    {c.note ? <Typography variant="caption" color="text.secondary" display="block">{c.note}</Typography> : null}
                  </TableCell>
                  <TableCell>{c.issuedBy?.email || c.issuedBy?.name || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={st.color} label={st.label} />
                  </TableCell>
                  <TableCell>
                    {(c.provisions || []).length
                      ? (c.provisions || []).map((p) => `${p.deviceCode || '?'} → ${p.wgIp || '?'}`).join(', ')
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {(c.status === 'active' || c.status === 'in_use') && (
                      <Button size="small" color="error" onClick={() => revoke(c._id)}>
                        취소
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default FieldCodePanel;
